import { useState, useEffect, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../styles/booking-search-flow.module.scss';
import HeaderFooterLayout from '../components/header-footer-layout';
import ConsoleLogoutPopup from '../pages/console-logout-popup';
import FormField from '../components/form-field';
import ComboboxFormField from '../components/combobox-form-field';
import ActionButton from '../components/action-button';
import DateRangeFormField from '../components/date-range-form-field';
import useBookingSearch, { type BookingSearchFormState } from '../hooks/use-booking-search';
import type { BookingSearchResult } from '../models/repair-bookings';
import BookingCancelDialog from '../pages/booking-cancel-dialog';
import ConsoleCompletion from '../pages/console-completion';
import { consoleCancelBooking } from '../utils/api.ts';
import { consoleLogout } from '../utils/paths';

type CancelState =
    | { status: 'idle' }
    | { status: 'confirm'; booking: BookingSearchResult }
    | { status: 'complete' };

const BookingConsoleSearchFlow = () => {
    const navigate = useNavigate();
    const csrfToken = window.preloadedState.environment.csrfToken;
    const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);
    const {
        form,
        bookings,
        isSearching,
        error,
        shopNames,
        onLastNamePronunciationChange,
        onFirstNamePronunciationChange,
        onPhoneNumberChange,
        onConfirmationIdChange,
        onShopNameChange,
        onDateRangeChange,
        search,
        clearSearch,
    } = useBookingSearch(csrfToken);

    const [cancelState, setCancelState] = useState<CancelState>({ status: 'idle' });

    useEffect(() => {
        if (error) {
            alert(error.message);
        }
    }, [error]);

    return (
        <>
            <HeaderFooterLayout
                title={
                    bookings !== null
                        ? '【 予約管理 】予約の確認／変更／キャンセル'
                        : '【 予約管理 】予約情報の入力'
                }
                withFooter={false}
                onHome={() => {
                    navigate('/console/bookings/');
                }}
                onLogout={() => {
                    setShowLogoutConfirmation(true);
                }}
            >
                {cancelState.status === 'complete' ? (
                    <ConsoleCompletion
                        onClose={() => {
                            navigate('/console/bookings/');
                        }}
                        onLogout={consoleLogout}
                    />
                ) : bookings !== null ? (
                    <BookingsList
                        bookings={bookings}
                        onBack={clearSearch}
                        onEdit={(booking) => {
                            navigate(`/console/bookings/${booking.booking_token}/`);
                        }}
                        onCancel={(booking) => {
                            setCancelState({ status: 'confirm', booking });
                        }}
                    />
                ) : (
                    <BookingsSearch
                        form={form}
                        isSearching={isSearching}
                        shopNames={shopNames}
                        onLastNamePronunciationChange={onLastNamePronunciationChange}
                        onFirstNamePronunciationChange={onFirstNamePronunciationChange}
                        onPhoneNumberChange={onPhoneNumberChange}
                        onConfirmationIdChange={onConfirmationIdChange}
                        onShopNameChange={onShopNameChange}
                        onDateRangeChange={onDateRangeChange}
                        search={search}
                    />
                )}
            </HeaderFooterLayout>
            {showLogoutConfirmation && (
                <ConsoleLogoutPopup
                    onOk={() => {
                        consoleLogout();
                        setShowLogoutConfirmation(false);
                    }}
                    onCancel={() => {
                        setShowLogoutConfirmation(false);
                    }}
                />
            )}
            {cancelState.status === 'confirm' && (
                <BookingCancelDialog
                    confirmationId={cancelState.booking.confirmation_id}
                    startAt={cancelState.booking.start_at}
                    shopName={cancelState.booking.shop_name}
                    name={`${cancelState.booking.last_name_pronunciation} ${cancelState.booking.first_name_pronunciation}`}
                    phoneNumber={cancelState.booking.phone_number}
                    onClose={() => setCancelState({ status: 'idle' })}
                    onExecute={async () => {
                        await consoleCancelBooking(csrfToken, cancelState.booking.booking_token);
                        setCancelState({ status: 'complete' });
                    }}
                />
            )}
        </>
    );
};

const BookingsSearch: FC<{
    form: BookingSearchFormState;
    isSearching: boolean;
    shopNames: string[];
    onLastNamePronunciationChange: (value: string) => void;
    onFirstNamePronunciationChange: (value: string) => void;
    onPhoneNumberChange: (value: string) => void;
    onConfirmationIdChange: (value: string) => void;
    onShopNameChange: (value: string) => void;
    onDateRangeChange: (fromDate: string, toDate: string) => void;
    search: () => Promise<BookingSearchResult[] | void>;
}> = ({
    form,
    isSearching,
    shopNames,
    onLastNamePronunciationChange,
    onFirstNamePronunciationChange,
    onPhoneNumberChange,
    onConfirmationIdChange,
    onShopNameChange,
    onDateRangeChange,
    search,
}) => {
    const navigate = useNavigate();
    const searchDisabled = Object.values(form).every((value) => value.trim() === '');

    return (
        <div className={styles.searchContents}>
            <div className={styles.topTitle}>予約情報を入力します</div>
            <div className={styles.formContainer}>
                <div className={styles.formRow}>
                    <FormField
                        inputType="text"
                        label="姓（カナ）"
                        placeholder="例）エイユウ"
                        value={form.lastNamePronunciation}
                        onChange={onLastNamePronunciationChange}
                    />
                    <FormField
                        inputType="text"
                        label="名（カナ）"
                        placeholder="例）サンタロウ"
                        value={form.firstNamePronunciation}
                        onChange={onFirstNamePronunciationChange}
                    />
                </div>
                <FormField
                    inputType="text"
                    label="携帯電話番号"
                    placeholder="例）000-000-0000"
                    value={form.phoneNumber}
                    onChange={onPhoneNumberChange}
                />
                <FormField
                    inputType="text"
                    label="予約番号"
                    placeholder="例）XXXXXXXXXXXXX"
                    value={form.confirmationId}
                    onChange={onConfirmationIdChange}
                />
                <div className={styles.separator} />
                <div className={styles.notice}>
                    姓名／電話番号／予約番号が不明瞭な際に、予約店舗名＋予約日付範囲で予約リストを検索できます
                </div>
                <div className={styles.formRow}>
                    <ComboboxFormField
                        label="予約店舗名"
                        placeholder="例）ペリエ千葉店"
                        value={form.shopName}
                        options={shopNames}
                        onChange={onShopNameChange}
                    />
                    <DateRangeFormField
                        label="予約日付範囲"
                        placeholder="日付範囲を選択してください"
                        dateRange={{ fromDate: form.fromDate, toDate: form.toDate }}
                        onChange={(newRange) => {
                            onDateRangeChange(newRange.fromDate, newRange.toDate);
                        }}
                    />
                </div>
            </div>
            <div className={styles.actions}>
                <ActionButton
                    title="戻る"
                    primary={false}
                    onClick={() => {
                        navigate('/console/bookings/');
                    }}
                />
                <ActionButton
                    title="検索する"
                    disabled={searchDisabled}
                    loading={isSearching}
                    onClick={search}
                />
            </div>
        </div>
    );
};

const BookingsList: FC<{
    bookings: BookingSearchResult[];
    onBack: () => void;
    onEdit: (booking: BookingSearchResult) => void;
    onCancel: (booking: BookingSearchResult) => void;
}> = ({ bookings, onBack, onEdit, onCancel }) => {
    return (
        <div className={styles.bookingsList}>
            {bookings.length === 0 ? (
                <div className={styles.notFoundNotice}>該当する予約が見つかりませんでした</div>
            ) : (
                bookings.map((booking) => (
                    <div className={styles.bookingCard} key={booking.confirmation_id}>
                        <div className={styles.bookingInfo}>
                            <div>
                                氏名：
                                {`${booking.last_name_pronunciation} ${booking.first_name_pronunciation}`}
                            </div>
                            <div>携帯電話番号：{booking.phone_number}</div>
                            <div>連絡先電話番号：{booking.alt_phone_number || '（なし）'}</div>
                            <div>予約番号：{booking.confirmation_id}</div>
                            <div>予約店舗：{booking.shop_name}</div>
                            <div>予約日時：{booking.start_at}</div>
                        </div>
                        <div className={styles.actionsArea}>
                            <div className={styles.lastUpdater}>
                                最終更新者：{booking.last_updater}
                            </div>
                            <ActionButton
                                className={styles.smallActionButton}
                                title="変更する"
                                onClick={() => {
                                    onEdit(booking);
                                }}
                            />
                            <ActionButton
                                className={styles.smallActionButton}
                                title="キャンセルする"
                                onClick={() => {
                                    onCancel(booking);
                                }}
                            />
                        </div>
                    </div>
                ))
            )}
            <div className={styles.listActions}>
                <ActionButton title="戻る" primary={false} onClick={onBack} />
            </div>
        </div>
    );
};

export default BookingConsoleSearchFlow;
